import { Loader2, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useQueryClient } from "@tanstack/react-query";
import { useResetShipping } from "@/hooks/use-shipping";
import { toast } from "sonner";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedOrderIds: string[];
  storeId: string;
  onSuccess: () => void;
};

export function ResetShippingDialog({ open, onOpenChange, selectedOrderIds, storeId, onSuccess }: Props) {
  const qc = useQueryClient();
  const resetMut = useResetShipping();

  async function handleReset() {
    try {
      await resetMut.mutateAsync({ orderIds: selectedOrderIds, storeId });
      await qc.invalidateQueries({ queryKey: ["orders"] });
      toast.success(`${selectedOrderIds.length} narudžbi resetovano`);
      onSuccess();
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.message || "Greška pri resetovanju pošiljki");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Resetuj dostavu</DialogTitle>
          <DialogDescription>{selectedOrderIds.length} narudžbi odabrano</DialogDescription>
        </DialogHeader>

        <div className="flex gap-3 items-start bg-yellow-500/10 border border-yellow-500/20 p-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600 shrink-0 mt-0.5" />
          <div className="text-xs text-yellow-700">
            <p className="font-medium mb-1">Upozorenje</p>
            <p>Ova akcija samo briše podatke o pošiljci iz sistema. Morate ručno otići na panel kurirske službe i obrisati paket prije resetovanja.</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Otkaži</Button>
          <Button variant="destructive" onClick={handleReset} disabled={resetMut.isPending}>
            {resetMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Resetuj
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
